// Pure CSV parser for practice-field permit windows. Header (required):
// subfield,weekday,start,end[,valid_from,valid_to] — weekday is Mon=1..Fri=5
// (practice never schedules weekends); start/end are zero-padded 24h HH:MM.
// Optional valid_from/valid_to are YYYY-MM-DD dates; blank means the season
// edge (NULL in the database). Bad lines are collected as errors (never thrown)
// and skipped from the parsed rows, so a mostly-good file still imports its
// good rows.
//
// Lives in shared-types rather than apps/workers because the admin SPA previews
// a chosen file with it before uploading. The Worker remains the authority —
// the SPA's preview is the same function, so the two cannot disagree about what
// a file contains.
//
// ⚠️ The import this feeds is APPEND-ONLY with no natural key (`upsertPermits`
// INSERTs a fresh UUID per row), so importing the same file twice silently
// doubles a season's field capacity. That is a UI concern, not a parser one,
// but it is why this parser is careful about the header: see below.

import { isIsoDate } from './practicePermitDates'
import { isHHMM } from './clock'

export interface PermitCsvRow {
  subfieldId: string
  weekday: number
  startTime: string
  endTime: string
  validFrom: string | null
  validTo: string | null
}

export interface PermitCsvResult {
  rows: PermitCsvRow[]
  errors: string[]
}

const HEADER = ['subfield', 'weekday', 'start', 'end']
const HEADER_DATED = [...HEADER, 'valid_from', 'valid_to']
/** Named, not written inline: U+FEFF renders as nothing, so a literal one
 *  inside a regex is invisible in an editor and un-greppable. Same constant and
 *  same reason as `packages/sheets/src/csv.ts`. */
const BOM = '﻿'

/** True when a line is the column header rather than data.
 *
 *  ⚠️ This used to be `lines.slice(1)` — the first line was dropped whether or
 *  not it was a header, so a headerless 177-row file imported 176 rows and said
 *  nothing about the one it ate. Pasting a file's contents usually carried the
 *  header along; choosing a file makes a headerless export just as likely, and
 *  a silently-dropped permit window is a field that quietly loses an hour of
 *  capacity for a season. */
function isHeaderLine(line: string): boolean {
  const cells = line.split(',').map((s) => s.trim().toLowerCase())
  const matches = (h: string[]) => cells.length === h.length && cells.every((c, i) => c === h[i])
  return matches(HEADER) || matches(HEADER_DATED)
}

/** Validates one already-split data line; returns the parsed row or an error
 *  string naming the 1-based line number **in the file**, so an operator can
 *  open the CSV in an editor and jump straight to it. */
function parseLine(line: string, lineNo: number): { row?: PermitCsvRow; error?: string } {
  const fields = line.split(',').map((s) => s.trim())
  if (fields.length !== 4 && fields.length !== 6) {
    return { error: `line ${lineNo}: expected 4 or 6 columns (subfield,weekday,start,end[,valid_from,valid_to]), got ${fields.length}` }
  }
  const [subfieldId, weekdayRaw, startTime, endTime, fromRaw = '', toRaw = ''] = fields
  const weekday = Number(weekdayRaw)
  if (!Number.isInteger(weekday) || weekday < 1 || weekday > 5) {
    return { error: `line ${lineNo}: weekday must be an integer 1-5 (Mon-Fri), got "${weekdayRaw}"` }
  }
  if (!startTime || !isHHMM(startTime) || !endTime || !isHHMM(endTime)) {
    return { error: `line ${lineNo}: start/end must be a zero-padded 24h time HH:MM` }
  }
  if (!(endTime > startTime)) {
    return { error: `line ${lineNo}: end (${endTime}) must be after start (${startTime})` }
  }
  if (!subfieldId) {
    return { error: `line ${lineNo}: subfield is required` }
  }
  const validFrom = fromRaw === '' ? null : fromRaw
  const validTo = toRaw === '' ? null : toRaw
  if (validFrom !== null && !isIsoDate(validFrom)) {
    return { error: `line ${lineNo}: valid_from must be a date YYYY-MM-DD, got "${fromRaw}"` }
  }
  if (validTo !== null && !isIsoDate(validTo)) {
    return { error: `line ${lineNo}: valid_to must be a date YYYY-MM-DD, got "${toRaw}"` }
  }
  if (validFrom !== null && validTo !== null && validFrom > validTo) {
    return { error: `line ${lineNo}: valid_from (${validFrom}) must not be after valid_to (${validTo})` }
  }
  return { row: { subfieldId, weekday, startTime, endTime, validFrom, validTo } }
}

/** Parses permit-window CSV text (header `subfield,weekday,start,end[,valid_from,valid_to]`,
 *  which is optional — a file that opens straight into data parses all of it).
 *  Blank lines are ignored; each bad data line produces one error and is excluded
 *  from `rows`, so `rows.length + errors.length` need not equal the number of
 *  lines when one is blank or is the header. */
export function parsePermitCsv(text: string): PermitCsvResult {
  // Excel writes a UTF-8 BOM on CSV export. Left in place it defeats the header
  // match, and the header then parses as data and reports a weekday error on
  // line 1 — a confusing first experience of an otherwise fine file.
  const lines = (text.startsWith(BOM) ? text.slice(BOM.length) : text).split(/\r?\n/)
  const rows: PermitCsvRow[] = []
  const errors: string[] = []
  // The header is recognized on the first NON-BLANK line, not at physical index
  // 0. Pasting into a textarea commonly picks up a leading newline, and some
  // exporters write one; keyed on index 0 the header then parsed as data and
  // reported `line 2: weekday must be an integer 1-5, got "weekday"` — the same
  // confusing first read of a fine file that the BOM strip above exists to
  // prevent.
  let seenData = false
  lines.forEach((line, i) => {
    if (line.trim().length === 0) return
    if (!seenData && isHeaderLine(line)) {
      seenData = true
      return
    }
    seenData = true
    const { row, error } = parseLine(line, i + 1)
    if (row) rows.push(row)
    if (error) errors.push(error)
  })
  return { rows, errors }
}
